import { useCallback, useSyncExternalStore } from 'react'
import { useTenant } from '@/providers/TenantProvider'

/**
 * The facility being looked at (z8nrz7fw25), one choice for the whole app:
 * switching it on Storage → Locations switches the facility set-up, the room
 * pickers and the 3D views with it, instead of each keeping its own.
 *
 * Kept per tenant in this browser (a convenience, never authoritative: an id
 * that no longer exists simply falls back to the first facility).
 */
const KEY = (tenantId: string) => `cgt.currentFacility.${tenantId}`
const listeners = new Set<() => void>()

function read(tenantId: string | null): string | null {
  if (!tenantId) return null
  try {
    return localStorage.getItem(KEY(tenantId))
  } catch {
    return null
  }
}

let memory: Record<string, string | null> = {}

export function useCurrentFacility(): [string | null, (id: string) => void] {
  const { selectedTenantId } = useTenant()
  const tenant = selectedTenantId ?? ''
  const id = useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => (tenant in memory ? memory[tenant] : read(selectedTenantId)),
  )
  const set = useCallback(
    (next: string) => {
      memory = { ...memory, [tenant]: next }
      try {
        localStorage.setItem(KEY(tenant), next)
      } catch {
        // Private mode or storage blocked: the choice still holds for this visit.
      }
      listeners.forEach((l) => l())
    },
    [tenant],
  )
  return [id, set]
}
